// = Libs =
import useSWR from "swr";
import formatDate from "@/helpers/formatDate";
import buildUrl from "build-url-ts";
import { CreatePollForm } from "@/pages/poll/new-poll/new-poll.types";
import sendRequest from "@/helpers/sendRequest";

// = Helpers =

interface ReportMiniModel {
	reportId: number;
	status: string;
}

interface PollMiniModel {
	pollId: number;
	title: string;
}

interface BEPollModel {
	pollId: number;
	title: string;
	status: string; //TODO: модель статусов
	description: string;
	startDate: string;
	finishDate: string;
	periodicType: string;
	report: ReportMiniModel;
}

interface BEFullPollModel {
	pollId: number;
	title: string;
	status: string;
	author: number;
	executors: number[];
	isPublic: boolean;
	description: string;
	startDate: string;
	finishDate: string;
	periodicType: string;
	report: ReportMiniModel;
}

const mapToExecutorCard = (el: BEPollModel) => {
	return {
		id: el.pollId,
		status: el.report ? el.report.status : 'new',
		title: el.title,
		mode: 'executor',
		availablePeriod: {
			start: formatDate(el.startDate),
			end: formatDate(el.finishDate),
		},
	};
};
const mapToAuthorCard = (el: BEPollModel) => {
	return {
		id: el.pollId,
		status: el.status,
		title: el.title,
		mode: 'author',
		availablePeriod: {
			start: formatDate(el.startDate),
			end: formatDate(el.finishDate),
		},
	};
};

interface FetchPollConfigInterface {
	limit: number;
	offset: number;
	mode: string;
	query?: string;
	orgId?: number;
	mapping: (el: BEPollModel) => any;
}

const usePollList = (config: FetchPollConfigInterface) => {
	const queryObj = {
		limit: config.limit,
		offset: config.offset,
		title: config.query,
	};

	console.log('fetching', config.mode, config.orgId)
	let queryString = buildUrl('', {
		path: config.orgId
			? `monitoring-api/polls/${config.mode}/${config.orgId}/`
			: 'monitoring-api/polls/',
		queryParams: queryObj,
	});
	if (config.mode === 'public') {
		queryString = buildUrl('', {
			path: `monitoring-api/polls/public/`,
			queryParams: queryObj,
		});
	}
	const { data, error } = useSWR(queryString);

	if (!data && !data?.items) return error;

	return {
		count: data?.items?.length,
		total: data?.items?.length,
		elementList: data?.items.map(config.mapping),
	};
};

const usePollData = (pollId: number | string): BEFullPollModel => {
	const { data, error } = useSWR(`monitoring-api/polls/${pollId}`);

	if (!data) return error;

	return data;
};

const usePollWithReportData = (
	pollId: number,
	orgId: number
): BEFullPollModel => {
	const { data, error } = useSWR(
		`monitoring-api/polls/${pollId}/with-report/${orgId}`
	);

	if (!data) return error;

	return data;
};

const createPoll = async (poll: CreatePollForm) => {
	let data = {
		'title': poll.title,
		'description': poll.description,
		'author': poll.author,
		'executors': poll.isPublic ? [] : poll.executors,
		'startDate': poll.startDate?.slice(0, 10),
		'finishDate': poll.finishDate?.slice(0, 10),
		'periodicType': poll.periodicType,
		'isPublic': poll.isPublic,
		'transmittalLetter':
			poll?.transmittalLetter === -1 ? null : poll?.transmittalLetter,
	};
	const { error, data: PollData } = await sendRequest<{ pollId: string }>({
		url: `/monitoring-api/polls/`,
		method: 'post',
		body: data,
	})
		.then((res) => res)
		.catch((err) => err);

	return { error, id: PollData?.pollId };
};

const updatePoll = async (poll: CreatePollForm, pollId: number) => {
	const data = {
		'title': poll.title,
		'description': poll.description,
		'author': poll.author,
		'executors': poll.isPublic ? [] : poll.executors,
		'startDate': poll.startDate?.slice(0, 10),
		'finishDate': poll.finishDate?.slice(0, 10),
		'periodicType': poll.periodicType,
		'isPublic': poll.isPublic,
		'transmittalLetter':
			poll?.transmittalLetter === -1 ? null : poll?.transmittalLetter,
	};
	const { error, data: PollData } = await sendRequest<{ pollId: string }>({
		url: `/monitoring-api/polls/${pollId}/`,
		method: 'put',
		body: data,
	})
		.then((res) => res)
		.catch((err) => err);

	return { error, id: PollData?.pollId };
};

const publishPoll = async (pollId: number) => {
	const { error, data } = await sendRequest({
		url: `/monitoring-api/polls/${pollId}/publish/`,
		method: 'patch',
	});
	return { error };
};
const deletePoll = async (pollId: number) => {
	const { error, data } = await sendRequest({
		url: `/monitoring-api/polls/${pollId}/`,
		method: 'delete',
	});
	return { error };
};

export {
	mapToExecutorCard,
	mapToAuthorCard,
	usePollList,
	usePollData,
	usePollWithReportData,
	createPoll,
	updatePoll,
	publishPoll,
	deletePoll,
};
export type {
	BEPollModel,
	BEFullPollModel,
	FetchPollConfigInterface,
	PollMiniModel,
};
